import { useRouter } from '@tanstack/react-router';
import { createContext, use } from 'react';
import { graphql, useFragment, useMutation, useQueryLoader } from 'react-relay';
import type {
    processDefinitionQueryDeleteMutation
} from './__generated__/processDefinitionQueryDeleteMutation.graphql';
import type {
    processDefinitionQueryDeployMutation
} from './__generated__/processDefinitionQueryDeployMutation.graphql';
import type {
    processDefinitionQueryFragment$key
} from './__generated__/processDefinitionQueryFragment.graphql';
import type {
    processDefinitionQueryMutation
} from './__generated__/processDefinitionQueryMutation.graphql';
import type {
    processDefinitionQueryStatusMutation
} from './__generated__/processDefinitionQueryStatusMutation.graphql';
import type {
    processDefinitionQueryResetMutation
} from './__generated__/processDefinitionQueryResetMutation.graphql';
import { processDefinitionRouteApi } from './process-definition-api';
import type {
    processDefinitionQueryIncidentFragment$key
} from './__generated__/processDefinitionQueryIncidentFragment.graphql.ts';
import type {
    processDefinitionQueryIncidentsQuery
} from './__generated__/processDefinitionQueryIncidentsQuery.graphql.ts'; // https://github.com/jakst/tanstack-router-relay/blob/main/src/routes/index.lazy.tsx

// https://github.com/jakst/tanstack-router-relay/blob/main/src/routes/index.lazy.tsx

// PANDING/LOADING STATE
// TSR - https://github.com/TanStack/router/blob/92c6c2b1ff3bbf661c6aed7fbda84c8233d0a30e/examples/react/kitchen-sink-file-based/src/routes/__root.tsx
// TSR - https://tanstack.com/router/latest/docs/framework/react/api/router/matchRouteComponent
// RR - https://www.geeksforgeeks.org/how-relay-is-different-from-redux/

export const ProcessDefinitionQueryCtx = createContext<
    processDefinitionQueryFragment$key | null | undefined
>(null);

export const ProcessDefinitionQueryIncidentCtx = createContext<
    processDefinitionQueryIncidentFragment$key | null | undefined
>(null);

const processDefinitionQueryFragment = graphql`
    fragment processDefinitionQueryFragment on ProcessDefinition {
        definitionKey
        definitionId
        definitionName
        definitionVersion
        definitionXml
        id
        siteUrl
        status
        dirtyFlag
        definitionHash
    }
`;

export const useProcessDefinitionQueryFragment = (ignoreIfNew: boolean = true) => {
    const fragment = useFragment(processDefinitionQueryFragment, use(ProcessDefinitionQueryCtx));
    const id = processDefinitionRouteApi.useParams({ select: (params) => params.id });
    if (ignoreIfNew && id === '-1') {
        return undefined;
    }
    return fragment;
};

export const processDefinitionQueryIncidentFragment = graphql`
    fragment processDefinitionQueryIncidentFragment on ProcessDefinitionIncident {
        processDefinitionId
        processInstanceId
        activityId
        failedActivityId
        incidentMessage
        incidentType
        incidentTimestamp
    }
`;

export const gqlIncidents = graphql`
    query processDefinitionQueryIncidentsQuery($definitionId: String!) {
        getProcessDefinitionIncidents(definitionId: $definitionId) {
            ...processDefinitionQueryIncidentFragment
        }
    }
`;

export const useProcessDefinitionIncidentQueryFragment = (ignoreIfNew: boolean = true) => {
    const fragment = useFragment(
        processDefinitionQueryIncidentFragment,
        use(ProcessDefinitionQueryIncidentCtx)
    );
    const id = processDefinitionRouteApi.useParams({ select: (params) => params.id });
    if (ignoreIfNew && id === '-1') {
        return undefined;
    }
    return fragment;
};

export const useProcessDefinitionIncidentsLoader = () =>
    useQueryLoader<processDefinitionQueryIncidentsQuery>(gqlIncidents);

const gqlMutation = graphql`
    mutation processDefinitionQueryMutation($input: ProcessDefinitionInput!) {
        saveProcessDefinition(input: $input) {
            id
        }
    }
`;

export const useProcessDefinitionMutation = () =>
    useMutation<processDefinitionQueryMutation>(gqlMutation);

const gqlDelete = graphql`
    mutation processDefinitionQueryDeleteMutation($id: ID!) {
        deleteProcessDefinition(id: $id)
    }
`;

export const useProcessDefinitionDelete = () =>
    useMutation<processDefinitionQueryDeleteMutation>(gqlDelete);

export const useProcessDefinitionQueryRefresh = (): (() => void) => {
    const router = useRouter();
    return () => {
        router.invalidate();
    };
};

const gqlChangeStatus = graphql`
    mutation processDefinitionQueryStatusMutation($id: ID!, $status: Status!) {
        changeProcessDefinitionStatus(ids: [$id], status: $status)
    }
`;

export const useProcessDefinitionChangeStatus = () =>
    useMutation<processDefinitionQueryStatusMutation>(gqlChangeStatus);

const gqlDeploy = graphql`
    mutation processDefinitionQueryDeployMutation($id: ID!) {
        deployProcessDefinition(id: $id)
    }
`;

export const useProcessDefinitionDeploy = () =>
    useMutation<processDefinitionQueryDeployMutation>(gqlDeploy);

const gqlReset = graphql`
    mutation processDefinitionQueryResetMutation($id: ID!) {
        resetProcessDefinition(id: $id) {
            definitionKey
            definitionName
            definitionVersion
            definitionXml
            id
            siteUrl
            status
            dirtyFlag
            definitionHash
        }
    }
`;

export const useProcessDefinitionsReset = () =>
    useMutation<processDefinitionQueryResetMutation>(gqlReset);
